import React, { useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { Science as TestIcon } from '@mui/icons-material';
import InlineAlert from '../../../../shared/components/InlineAlert';
import EventPatternEditor from '../EventPatternEditor';
import TestEventEditor from '../TestEventEditor';
import PatternTestResult from '../PatternTestResult';
import { usePatternTest } from '../../hooks/usePatternTest';

interface DatasetPatternSectionProps {
  customerId: string;
  eventPattern: Record<string, unknown>;
  sampleEvent: Record<string, unknown>;
  locked: boolean;
  errors: Record<string, string>;
  onPatternChange: (pattern: Record<string, unknown>) => void;
  onPatternValidityChange: (valid: boolean) => void;
  onSampleChange: (sample: Record<string, unknown>) => void;
}

/** The events the dataset collects, tested against a sample. */
const DatasetPatternSection: React.FC<DatasetPatternSectionProps> = ({
  customerId,
  eventPattern,
  sampleEvent,
  locked,
  errors,
  onPatternChange,
  onPatternValidityChange,
  onSampleChange,
}) => {
  const { runTest, testing, result, error, setError } = usePatternTest(customerId);
  const [sampleValid, setSampleValid] = useState(true);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <EventPatternEditor
        value={eventPattern}
        onChange={onPatternChange}
        onValidityChange={onPatternValidityChange}
        error={errors.eventPattern}
        readOnly={locked}
      />
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Test against a sample event; the column paths below are picked from it
        </Typography>
        <TestEventEditor value={sampleEvent} onChange={onSampleChange} onValidityChange={setSampleValid} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={testing ? <CircularProgress size={16} /> : <TestIcon />}
            onClick={() => runTest(eventPattern, sampleEvent)}
            disabled={testing || !sampleValid}
          >
            {testing ? 'Testing…' : 'Test match'}
          </Button>
          {result && <PatternTestResult result={result} />}
        </Box>
        {error && (
          <InlineAlert severity="error" onClose={() => setError(null)} sx={{ mt: 1.5 }}>
            {error}
          </InlineAlert>
        )}
      </Box>
    </Box>
  );
};

export default DatasetPatternSection;
